import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { Subscription } from 'rxjs';
import * as moment from 'moment';

import {
    ClientFeedSubscriptionModel,
    HttpStatusCodesEnum,
    ServerResponseData,
    SesService,
    SubscriptionDeactivationEligibleCountInterface,
    SubscriptionDeactivationResponseInterface,
    SubscriptionDeactivationResultEnum,
    UserAccountSubscriptionInterface
} from '@spa/api';

import {
    DialogActionsEnum,
    DialogButtonTypesEnum,
    DialogComponent,
    DialogConfigInterface
} from 'appShared/components';
import { DatepickerControl, Helper } from 'appShared/classes';
import { AlertsService } from 'appShared/services';
import { AlertTypesEnum, ComponentTypesEnum } from 'appShared/enums';
import { environment } from 'appEnv/environment';

import { MarketDataFeedUserTypesEnum } from 'appEntries/panel/enums';

import { SubscriptionDeactivationHelper } from './subscription-deactivation.helper';

export const DEACTIVATION_DATE_CONTROL = 'ScheduleDeactivationDate';
const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Deactivate Feeds/Bundles dialog (CRM-5195).
 *
 * Presents the user/account group's eligible (active/future) subscriptions
 * pre-selected, a Select all/Clear all toggle, a mandatory Schedule Deactivation
 * date picker (default/min = the operator's current local day, no maximum, date
 * granularity only), and the required warnings. On confirm it converts the
 * operator-selected local date to the covering UTC date client-side and submits a
 * single request through the shared `SesService` deactivation endpoint, whose mode
 * is derived from the selection (full list -> ALL_FOR_USER_ACCOUNT; any unchecked
 * -> SELECTED).
 *
 * All non-trivial logic is delegated to the unit-tested helper; this component
 * only wires it to the dialog UI and does not reproduce the server-owned
 * state-transition matrix.
 */
@Component({
    selector: 'app-market-data-deactivate-subscription',
    templateUrl: './market-data-deactivate-subscription.component.html',
    styleUrls: ['./market-data-deactivate-subscription.component.scss']
})
export class MarketDataDeactivateSubscriptionComponent
    extends DialogComponent<MarketDataDeactivateSubscriptionComponent, boolean> implements OnInit, OnDestroy {

    public model: ClientFeedSubscriptionModel;
    public userType: MarketDataFeedUserTypesEnum;

    public eligibleSubscriptions: UserAccountSubscriptionInterface[];
    public selectedIds: number[];
    public previewCounts: SubscriptionDeactivationEligibleCountInterface;

    public effectiveDateControl: DatepickerControl;
    public formGroup: FormGroup;

    private subscriptions: Subscription[];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            config: DialogConfigInterface,
            model: ClientFeedSubscriptionModel,
            userType: MarketDataFeedUserTypesEnum
        },
        public dialogRef: MatDialogRef<MarketDataDeactivateSubscriptionComponent>,
        private readonly sesService: SesService,
        private readonly alertsService: AlertsService
    ) {
        super(dialogRef);

        this.config = data.config;
        this.model = data.model;
        this.userType = data.userType;
        this.subscriptions = [];
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.eligibleSubscriptions = SubscriptionDeactivationHelper.getEligibleSubscriptions(this.model.subscriptions);
        this.selectedIds = this.eligibleSubscriptions.map(subscription => subscription.id);

        this.initDateControl();
        this.loadPreviewCounts();
        this.refreshConfirmState();

        this.subscriptions.push(
            this.formGroup.valueChanges.subscribe(() => this.refreshConfirmState())
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription && subscription.unsubscribe());
    }

    get eligibleIds(): number[] {
        return this.eligibleSubscriptions.map(subscription => subscription.id);
    }

    get selectedCount(): number {
        return this.selectedIds.length;
    }

    get activeEligibleCount(): number {
        return this.eligibleSubscriptions.filter(subscription => subscription.active).length;
    }

    get futureEligibleCount(): number {
        return this.eligibleSubscriptions.filter(subscription => subscription.future).length;
    }

    get isAllSelected(): boolean {
        return this.eligibleIds.length > 0 && this.selectedCount === this.eligibleIds.length;
    }

    get userFullName(): string {
        return `${this.model.userFirstName} ${this.model.userLastName}`;
    }

    isSelected(id: number): boolean {
        return this.selectedIds.indexOf(id) !== -1;
    }

    isSubscriptionActive(subscription: UserAccountSubscriptionInterface): boolean {
        return subscription.active;
    }

    toggleSubscription(id: number): void {
        if (this.isSelected(id)) {
            this.selectedIds = this.selectedIds.filter(selectedId => selectedId !== id);
        } else {
            this.selectedIds = [...this.selectedIds, id];
        }

        this.refreshConfirmState();
    }

    toggleSelectAll(): void {
        this.selectedIds = this.isAllSelected ? [] : this.eligibleIds;

        this.refreshConfirmState();
    }

    onAction(action: number): void {
        if (action !== DialogActionsEnum.Confirm) {
            this.dialogRef.close(false);

            return;
        }

        if (!this.canSubmit()) {
            return;
        }

        const submittedAsAll = SubscriptionDeactivationHelper.isFullSelection(this.eligibleIds, this.selectedIds);

        const request = SubscriptionDeactivationHelper.buildDeactivationRequest({
            userId: String(this.model.userId),
            // SES keys the scope by the account reference (the eligible-count /
            // deactivation examples use accountId = e.g. "ACC-1001").
            accountId: this.model.accountReference,
            effectiveDate: this.getEffectiveUtcDate(),
            eligibleIds: this.eligibleIds,
            selectedIds: this.selectedIds,
            expectedActiveCount: this.getExpectedActiveCount(),
            expectedFutureCount: this.getExpectedFutureCount()
        });

        if (!request) {
            return;
        }

        this.startLoading(() => {
            this.subscriptions.push(
                this.sesService.deactivateUserAccountSubscriptions(request).subscribe(
                    (response: ServerResponseData<SubscriptionDeactivationResponseInterface>) =>
                        this.onDeactivationResponse(response, submittedAsAll),
                    () => this.onUnexpectedError()
                )
            );
        });
    }

    private onDeactivationResponse(
        response: ServerResponseData<SubscriptionDeactivationResponseInterface>,
        submittedAsAll: boolean
    ): void {
        if (response.status === HttpStatusCodesEnum.Ok) {
            this.onDeactivationSuccess(response.data as SubscriptionDeactivationResponseInterface);

            return;
        }

        this.onDeactivationError(response, submittedAsAll);
    }

    private onDeactivationSuccess(response: SubscriptionDeactivationResponseInterface): void {
        if (response.resultCode === SubscriptionDeactivationResultEnum.NoEligibleSubscriptions) {
            this.alertsService.addAlert(
                response.message || 'No eligible subscriptions were found for this user and account.',
                AlertTypesEnum.Warning
            );

            this.dialogRef.close(true);

            return;
        }

        const parts = [
            `${response.activeScheduledCount} scheduled`,
            `${response.futureCancelledCount} cancelled`
        ];

        if (response.redefinedCount > 0) {
            parts.push(`${response.redefinedCount} rescheduled`);
        }

        let message = `Deactivation applied (${parts.join(', ')}); end date ${response.appliedEndDate} (UTC).`;

        if (response.dateAdjusted) {
            message += ' The submitted date was earlier than the current server day and was adjusted to it.';
        }

        this.alertsService.addAlert(message, AlertTypesEnum.Success);

        this.dialogRef.close(true);
    }

    private onDeactivationError(
        response: ServerResponseData<SubscriptionDeactivationResponseInterface>,
        submittedAsAll: boolean
    ): void {
        this.stopLoading();

        const message = response.messages && response.messages.length ? response.messages[0] : null;

        // The ALL_FOR_USER_ACCOUNT eligible-count-changed error carries only the
        // refreshed counts and no `errors[]`, so the shared error handler drops
        // its body (no message survives). Recover by re-reading the authoritative
        // counts and asking the operator to reconfirm (AC-36).
        if (!message && submittedAsAll) {
            this.refreshPreviewForReconfirmation();

            return;
        }

        this.alertsService.addAlert(message || 'Deactivation failed. Please reload and try again.', AlertTypesEnum.Error);
    }

    private refreshPreviewForReconfirmation(): void {
        this.subscriptions.push(
            this.sesService.getSubscriptionDeactivationEligibleCount(
                String(this.model.userId),
                this.model.accountReference
            ).subscribe((response: ServerResponseData<SubscriptionDeactivationEligibleCountInterface>) => {
                if (response.status === HttpStatusCodesEnum.Ok) {
                    this.previewCounts = response.data as SubscriptionDeactivationEligibleCountInterface;
                }

                this.alertsService.addAlert(
                    'Eligible subscription counts have changed; please review the refreshed counts and reconfirm.',
                    AlertTypesEnum.Warning
                );
            })
        );
    }

    private onUnexpectedError(): void {
        this.stopLoading();
        this.alertsService.addAlert('Deactivation failed. Please reload and try again.', AlertTypesEnum.Error);
    }

    private canSubmit(): boolean {
        return this.selectedCount > 0 && !!this.getSelectedCalendarDate();
    }

    private refreshConfirmState(): void {
        const confirmButton = this.config.buttons.find(button => button.type === DialogButtonTypesEnum.Confirm);

        if (!confirmButton) {
            return;
        }

        confirmButton.hidden = this.selectedCount === 0;
        confirmButton.disabled = !this.canSubmit();
    }

    private initDateControl(): void {
        this.effectiveDateControl = new DatepickerControl({
            name: DEACTIVATION_DATE_CONTROL,
            value: Helper.formatDateByValueOrNow(null, environment.dateFormatLocalDate, true),
            placeholder: 'Schedule Deactivation',
            validators: [Validators.required],
            componentType: ComponentTypesEnum.Datepicker,
            minDate: moment(new Date()).format(DATE_FORMAT),
            maxDate: null,
            floatingLabelNever: false
        });

        this.formGroup = new FormGroup({
            [this.effectiveDateControl.name]: new FormControl(
                this.effectiveDateControl.value,
                this.effectiveDateControl.validators
            )
        });
    }

    private loadPreviewCounts(): void {
        this.subscriptions.push(
            this.sesService.getSubscriptionDeactivationEligibleCount(
                String(this.model.userId),
                this.model.accountReference
            ).subscribe(
                (response: ServerResponseData<SubscriptionDeactivationEligibleCountInterface>) => {
                    this.previewCounts = response.status === HttpStatusCodesEnum.Ok
                        ? response.data as SubscriptionDeactivationEligibleCountInterface
                        : null;
                },
                () => this.previewCounts = null
            )
        );
    }

    private getExpectedActiveCount(): number {
        return this.previewCounts ? this.previewCounts.activeCount : this.activeEligibleCount;
    }

    private getExpectedFutureCount(): number {
        return this.previewCounts ? this.previewCounts.futureCount : this.futureEligibleCount;
    }

    /** The operator-selected local calendar date (YYYY-MM-DD), or null. */
    private getSelectedCalendarDate(): string {
        const value = this.formGroup.get(this.effectiveDateControl.name).value;

        return value ? String(value).slice(0, 10) : null;
    }

    private getEffectiveUtcDate(): string {
        return Helper.toUtcEndOfDayDate(this.getSelectedCalendarDate());
    }
}
